import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ENROLLMENT_STATUS_MAP, MAX_RATING } from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { date, money, num, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { SessionDialog, type NeedRow, type SessionRow } from './Learning';

interface EnrollmentRow {
  id: string;
  status: 'ENROLLED' | 'ATTENDED' | 'ABSENT' | 'CANCELLED';
  statusLabel: string;
  statusKind: string;
  statusNote: string;
  holdsSeat: boolean;
  score: number | null;
  passed: boolean | null;
  rating: number | null;
  feedback: string | null;
  certificateNo: string | null;
  employee: {
    id: string;
    name: string;
    code: string | null;
    department: string | null;
  };
  need: { id: string; title: string } | null;
  session: {
    id: string;
    startsOn: string | null;
    endsOn: string | null;
    course: { id: string; title: string };
  };
}

interface EmployeeLite {
  id: string;
  name: string;
  code: string | null;
}

/**
 * انعقادُ دورة.
 *
 * ## ولماذا لا يُغلق وفيه مقعدٌ لم يُحسم
 *
 * «انتهت» وفيها عشرةٌ ما زالوا «مقيَّدين» تعني عشرةً لا يُعرف أحضروا أم
 * لا، ثمّ تُقرأ نسبةُ الحضور على أنّها صفر. ومن غاب يُعلَّم غائبًا: مقعدُه
 * دُفع ثمنُه وأُهدر، وذاك رقمٌ يغيّر كيف تُحجز المقاعد في المرّة القادمة.
 */
export function TrainingSession() {
  const { id = '' } = useParams();
  const { atLeast } = useAuth();
  const canAdmin = atLeast('ADMIN');
  const queryClient = useQueryClient();
  const toast = useToast();

  const [enrolling, setEnrolling] = useState(false);
  const [editing, setEditing] = useState(false);
  const [result, setResult] = useState<EnrollmentRow | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const session = useQuery({
    queryKey: ['learning', 'session', id],
    queryFn: () => api<SessionRow>(`/hr/learning/sessions/${id}`),
  });

  const seats = useQuery({
    queryKey: ['learning', 'enrollments', { sessionId: id }],
    queryFn: () =>
      api<EnrollmentRow[]>(`/hr/learning/enrollments?sessionId=${id}`),
  });

  const setStatus = useMutation({
    mutationFn: (status: string) =>
      api(`/hr/learning/sessions/${id}/status`, {
        method: 'POST',
        body: { status },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['learning'] });
      setCancelling(false);
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const s = session.data;
  const rows = seats.data ?? [];

  return (
    <div className="page">
      <PageHead
        title={s?.course.title ?? 'انعقاد'}
        subtitle={
          s
            ? `${date(s.startsOn ?? '')} — ${date(s.endsOn ?? '')}${s.trainer ? ` · ${s.trainer}` : ''}`
            : undefined
        }
        actions={
          canAdmin &&
          s && (
            <div className="row" style={{ gap: 6 }}>
              {s.status !== 'DONE' && s.status !== 'CANCELLED' && (
                <button className="btn" onClick={() => setEditing(true)}>
                  <Icon name="edit" size={14} />
                  تعديل
                </button>
              )}
              {s.status === 'PLANNED' && (
                <button
                  className="btn btn-primary"
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate('RUNNING')}
                >
                  بدأت
                </button>
              )}
              {s.status === 'RUNNING' && (
                <button
                  className="btn btn-primary"
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate('DONE')}
                >
                  <Icon name="check" size={14} />
                  انتهت
                </button>
              )}
              {(s.status === 'PLANNED' || s.status === 'RUNNING') && (
                <button
                  className="btn btn-danger"
                  onClick={() => setCancelling(true)}
                >
                  ألغِه
                </button>
              )}
            </div>
          )
        }
      />

      <Link className="link tiny" to="/hr/learning">
        <Icon name="arrowLeft" size={12} /> التدريب والتطوير
      </Link>

      {session.isError && (
        <ErrorState
          error={session.error}
          onRetry={() => void session.refetch()}
        />
      )}
      {session.isPending && (
        <div className="skeleton" style={{ height: 180 }} />
      )}

      {s && (
        <>
          <div className="stats mt-3">
            <Stat
              label="الحالة"
              value={<Badge kind={s.statusKind}>{s.statusLabel}</Badge>}
              note={s.statusNote}
            />
            <Stat
              label="المقاعد"
              value={
                s.capacity === null
                  ? num(s.seats)
                  : `${num(s.seats)} / ${num(s.capacity)}`
              }
              note={
                s.left === null
                  ? 'بلا حدٍّ للمقاعد'
                  : `بقي ${num(s.left)} مقعدًا`
              }
            />
            <Stat
              label="حضر"
              value={num(s.attended)}
              note={
                s.pending > 0
                  ? `و${num(s.pending)} لم يُحسم بعد`
                  : 'كلُّ مقعدٍ حُسم'
              }
            />
            <Stat
              label="كلفة المقعد"
              value={
                s.costPerSeat === null ? '—' : money(s.costPerSeat, s.currency)
              }
              note={
                s.cost === null
                  ? 'لا تكلفةَ مكتوبة'
                  : `من ${money(s.cost, s.currency)} على من شغل مقعدًا`
              }
            />
          </div>

          {s.pending > 0 && s.status === 'RUNNING' && (
            <Notice title="مقاعدُ لم تُحسم" icon="alert" kind="warning">
              {plural(s.pending, WORDS.record)} لم يُعلَّم أحضر صاحبُه أم غاب.
              والانعقادُ لا يُغلق حتى يُحسم كلُّ مقعد — وإلّا قُرئت نسبةُ الحضور
              على أنّها صفر.
            </Notice>
          )}

          {s.notes && (
            <Notice title="ملاحظات" icon="note">
              {s.notes}
            </Notice>
          )}

          <div className="card mt-3">
            <div className="row" style={{ gap: 'var(--sp-2)' }}>
              <h2 className="card-title" style={{ flex: 1 }}>
                المقاعد
              </h2>
              {s.enrolling && (
                <button
                  className="btn btn-primary"
                  onClick={() => setEnrolling(true)}
                >
                  <Icon name="plus" size={15} />
                  قيّد مقاعد
                </button>
              )}
            </div>

            {seats.isPending && (
              <div className="skeleton" style={{ height: 140 }} />
            )}

            {seats.data && rows.length === 0 && (
              <EmptyState
                title="لا مقاعد"
                text="قيّد من يحضر — ومن كُتب له احتياجٌ يُلحَق به فيُغلق احتياجُه بحضوره."
              />
            )}

            {rows.length > 0 && (
              <div className="table-wrap sticky-head mt-3">
                <table className="table">
                  <thead>
                    <tr>
                      <th>الموظّف</th>
                      <th>القسم</th>
                      <th>الاحتياج</th>
                      <th>الدرجة</th>
                      <th>تقديرُه للدورة</th>
                      <th>الشهادة</th>
                      <th>الحالة</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((e) => (
                      <tr key={e.id}>
                        <td>{e.employee.name}</td>
                        <td className="tiny">{e.employee.department ?? '—'}</td>
                        <td className="tiny">{e.need?.title ?? '—'}</td>
                        <td className="num">
                          {e.score === null ? '—' : num(e.score)}
                          {e.passed === true && (
                            <div className="tiny mute">اجتاز</div>
                          )}
                          {e.passed === false && (
                            <div className="tiny mute">لم يجتز</div>
                          )}
                        </td>
                        <td className="num">
                          {e.rating === null
                            ? '—'
                            : `${num(e.rating)} / ${num(MAX_RATING)}`}
                        </td>
                        <td className="tiny">{e.certificateNo ?? '—'}</td>
                        <td>
                          <Badge kind={e.statusKind}>{e.statusLabel}</Badge>
                        </td>
                        <td>
                          {s.status !== 'CANCELLED' && (
                            <button
                              className="btn btn-sm"
                              onClick={() => setResult(e)}
                            >
                              {s.results ? 'سجّل النتيجة' : 'عدّل القيد'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {enrolling && s && (
        <EnrollDialog session={s} onClose={() => setEnrolling(false)} />
      )}
      {editing && s && (
        <SessionDialog session={s} onClose={() => setEditing(false)} />
      )}
      {result && s && (
        <ResultDialog
          enrollment={result}
          canMarkAttendance={s.results}
          onClose={() => setResult(null)}
        />
      )}

      {cancelling && (
        <Modal
          title="إلغاء الانعقاد"
          onClose={() => setCancelling(false)}
          footer={
            <>
              <button className="btn" onClick={() => setCancelling(false)}>
                تراجع
              </button>
              <button
                className="btn btn-danger"
                disabled={setStatus.isPending}
                onClick={() => setStatus.mutate('CANCELLED')}
              >
                ألغِه
              </button>
            </>
          }
        >
          <Notice title="ما يقع عند الإلغاء" icon="alert" kind="warning">
            تُلغى المقاعدُ كلُّها، ويعود كلُّ احتياجٍ أُلحق بها «مكتوبًا» — فلا
            يخرج من ينتظر دورةً من قائمة من يحتاج عملًا. والإلغاءُ لا رجعةَ فيه:
            من أراد إعادتها يكتب انعقادًا جديدًا.
          </Notice>
        </Modal>
      )}
    </div>
  );
}

/* ==========================================================================
   قيد المقاعد
   ========================================================================== */

function EnrollDialog({
  session,
  onClose,
}: {
  session: SessionRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [picked, setPicked] = useState<string[]>([]);
  const [needId, setNeedId] = useState('');

  const staff = useQuery({
    queryKey: ['employees', { status: 'ACTIVE', pageSize: 200 }],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>(
        '/hr/employees?status=ACTIVE&pageSize=200',
      ),
  });

  /*
   * الاحتياجاتُ المكتوبةُ تُعرض ليُلحَق بها المقعد.
   *
   * وإلحاقُه هو ما يجعل حضورَه يُغلق الاحتياجَ بواقعةٍ لا بيد — وتقريرُ
   * التدريب يقول إنّ فجوةً سُدّت ويشهد النظامُ كيف.
   */
  const needs = useQuery({
    queryKey: ['learning', 'needs', { status: 'OPEN' }],
    queryFn: () => api<NeedRow[]>('/hr/learning/needs?status=OPEN'),
  });

  const enroll = useMutation({
    mutationFn: () =>
      api<{ added: number; skipped: number }>(
        `/hr/learning/sessions/${session.id}/enroll`,
        {
          method: 'POST',
          body: { employeeIds: picked, needId: needId || null },
        },
      ),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ['learning'] });
      toast(
        r.skipped > 0
          ? `قُيّد ${num(r.added)} · و${num(r.skipped)} مقيَّدٌ من قبل`
          : `قُيّد ${num(r.added)}`,
      );
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر القيد'),
  });

  const toggle = (id: string) =>
    setPicked((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    );

  const tooMany = session.left !== null && picked.length > session.left;

  return (
    <Modal
      title="قيد مقاعد"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={enroll.isPending || picked.length === 0 || tooMany}
            onClick={() => enroll.mutate()}
          >
            قيّد {picked.length > 0 ? `(${num(picked.length)})` : ''}
          </button>
        </>
      }
    >
      <div className="field">
        <label className="label" htmlFor="te-need">
          الاحتياج الذي يُلحَق به
        </label>
        <select
          id="te-need"
          className="select"
          value={needId}
          onChange={(e) => setNeedId(e.target.value)}
        >
          <option value="">— بلا احتياج —</option>
          {(needs.data ?? []).map((n) => (
            <option key={n.id} value={n.id}>
              {n.title}
              {n.employee ? ` — ${n.employee.name}` : ''}
              {n.position ? ` — ${n.position.title}` : ''}
            </option>
          ))}
        </select>
        <span className="tiny mute">
          بإلحاقه يُغلق الاحتياجُ حين يُسجَّل حضورُ صاحبه — لا بيد.
        </span>
      </div>

      {session.left !== null && (
        <p className="tiny mute mt-2">
          بقي {num(session.left)} مقعدًا.
          {tooMany ? ' واخترتَ أكثرَ منها.' : ''}
        </p>
      )}

      <div className="table-wrap mt-3" style={{ maxHeight: 320 }}>
        <table className="table">
          <thead>
            <tr>
              <th />
              <th>الموظّف</th>
              <th>الكود</th>
            </tr>
          </thead>
          <tbody>
            {(staff.data?.items ?? []).map((e) => (
              <tr key={e.id}>
                <td style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    aria-label={`قيّد ${e.name}`}
                    checked={picked.includes(e.id)}
                    onChange={() => toggle(e.id)}
                  />
                </td>
                <td>{e.name}</td>
                <td className="tiny mute">{e.code ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="tiny mute mt-2">
        القائمةُ أوّلُ مئتي موظّفٍ على رأس العمل — ومن قُيّد من قبلُ يُتجاوَز
        بلا خطأ.
      </p>
    </Modal>
  );
}

/* ==========================================================================
   النتيجة
   ========================================================================== */

function ResultDialog({
  enrollment,
  canMarkAttendance,
  onClose,
}: {
  enrollment: EnrollmentRow;
  canMarkAttendance: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [status, setStatus] = useState(enrollment.status);
  const [score, setScore] = useState(
    enrollment.score === null ? '' : String(enrollment.score),
  );
  const [passed, setPassed] = useState<string>(
    enrollment.passed === null ? '' : enrollment.passed ? '1' : '0',
  );
  const [rating, setRating] = useState(
    enrollment.rating === null ? '' : String(enrollment.rating),
  );
  const [feedback, setFeedback] = useState(enrollment.feedback ?? '');
  const [certificateNo, setCertificateNo] = useState(
    enrollment.certificateNo ?? '',
  );

  const save = useMutation({
    mutationFn: () =>
      api(`/hr/learning/enrollments/${enrollment.id}/result`, {
        method: 'POST',
        body: {
          status,
          score: score === '' ? null : Number(score),
          passed: passed === '' ? null : passed === '1',
          rating: rating === '' ? null : Number(rating),
          feedback: feedback || null,
          certificateNo: certificateNo || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['learning'] });
      toast('حُفظت النتيجة.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const options = canMarkAttendance
    ? (['ATTENDED', 'ABSENT', 'CANCELLED'] as const)
    : (['ENROLLED', 'CANCELLED'] as const);

  return (
    <Modal
      title={`مقعد ${enrollment.employee.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            احفظ
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label" htmlFor="tr-status">
            الحالة
          </label>
          <select
            id="tr-status"
            className="select"
            value={status}
            onChange={(e) =>
              setStatus(e.target.value as EnrollmentRow['status'])
            }
          >
            {options.map((k) => (
              <option key={k} value={k}>
                {ENROLLMENT_STATUS_MAP[k].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            {ENROLLMENT_STATUS_MAP[status].description}
          </span>
        </div>

        {enrollment.need && status === 'ATTENDED' && (
          <div className="field span-2">
            <Notice title="ويُغلق احتياجُه" icon="check" kind="success">
              «{enrollment.need.title}» يُعلَّم «سُدّ» بتسجيل حضوره — وهذا ما
              يشهد كيف سُدّ.
            </Notice>
          </div>
        )}

        {enrollment.need && (status === 'ABSENT' || status === 'CANCELLED') && (
          <div className="field span-2">
            <Notice title="ويعود احتياجُه مكتوبًا" icon="info">
              «{enrollment.need.title}» يعود إلى قائمة ما ينتظر عملًا: الفجوةُ
              لم تُسدّ.
            </Notice>
          </div>
        )}

        {canMarkAttendance && (
          <>
            <div className="field">
              <label className="label" htmlFor="tr-score">
                درجة الاختبار
              </label>
              <input
                id="tr-score"
                className="input num"
                type="number"
                min={0}
                max={100}
                value={score}
                onChange={(e) => setScore(e.target.value)}
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="tr-passed">
                النتيجة
              </label>
              <select
                id="tr-passed"
                className="select"
                value={passed}
                onChange={(e) => setPassed(e.target.value)}
              >
                <option value="">— لا اختبار —</option>
                <option value="1">اجتاز</option>
                <option value="0">لم يجتز</option>
              </select>
            </div>

            <div className="field">
              <label className="label" htmlFor="tr-rating">
                تقديرُه للدورة
              </label>
              <input
                id="tr-rating"
                className="input num"
                type="number"
                min={1}
                max={MAX_RATING}
                value={rating}
                onChange={(e) => setRating(e.target.value)}
              />
              {/*
                رأيُ المتدرّب يُكتب هنا بيدٍ لأنّه لا بابَ للموظّف في هذا
                النظام يجيب منه — ويُقال ذلك بدل استمارةٍ لا تصل أحدًا.
              */}
              <span className="tiny mute">
                يُنقل عنه بيد — لا استمارةَ تصل المتدرّبَ في هذا النظام.
              </span>
            </div>

            <div className="field">
              <label className="label" htmlFor="tr-cert">
                رقم الشهادة
              </label>
              <input
                id="tr-cert"
                className="input"
                value={certificateNo}
                onChange={(e) => setCertificateNo(e.target.value)}
              />
            </div>

            <div className="field span-2">
              <label className="label" htmlFor="tr-feedback">
                ما قاله عنها
              </label>
              <textarea
                id="tr-feedback"
                className="input"
                rows={2}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
              />
            </div>
          </>
        )}

        {!canMarkAttendance && (
          <div className="field span-2">
            <p className="tiny mute">
              الانعقادُ لم يبدأ بعد، والحضورُ واقعةٌ لا تُسجَّل قبل وقوعها.
              علّمه «بدأت» ثمّ سجّل من حضر ومن غاب.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
